/**
 * Google sign-in, browser half: Google Identity Services.
 *
 * This one is COMPLETE — it works the moment `GOOGLE_CLIENT_ID` is filled in, with no
 * further edits. What it returns is Google's ID token (a signed JWT), which is exactly
 * the `credential` `POST /auth/oauth/exchange` verifies; the gateway checks Google's
 * signature itself, so nothing here has to be trusted beyond "it came from
 * accounts.google.com".
 *
 * GIS is loaded on demand rather than from a `<script>` in index.html: a sign-in
 * screen must not wait on a third-party host for its first paint, and a deployment
 * with no client id must not talk to Google at all.
 *
 * `prompt()` rather than a rendered Google button: the app has one button style and
 * one palette, and GIS's own button cannot be made to match either. The trade is that
 * a browser blocking third-party cookies may suppress One Tap — hence `select_account`
 * as the fallback path below, which is a full popup and is not affected.
 */
import {GOOGLE_CLIENT_ID} from '@/shared/config/oauth';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

type GisCredentialResponse = {credential?: string};

type Gis = {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string;
        callback: (response: GisCredentialResponse) => void;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
      }) => void;
      prompt: (listener?: (notification: unknown) => void) => void;
    };
  };
};

let loading: Promise<Gis> | null = null;

function loadGis(): Promise<Gis> {
  if (loading) {
    return loading;
  }
  loading = new Promise<Gis>((resolve, reject) => {
    const existing = (globalThis as {google?: Gis}).google;
    if (existing?.accounts?.id) {
      resolve(existing);
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const gis = (globalThis as {google?: Gis}).google;
      if (gis?.accounts?.id) {
        resolve(gis);
      } else {
        reject(new Error('google identity services loaded without accounts.id'));
      }
    };
    script.onerror = () => {
      // Reset so a retry after the network comes back can load it again.
      loading = null;
      reject(new Error('google identity services failed to load'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

/** True when this platform can actually run a sign-in flow. */
export const GOOGLE_SIGNIN_AVAILABLE = true;

/**
 * Run the flow and return Google's ID token, or null when the user dismissed it.
 *
 * Null is not an error and must not be shown as one: closing the Google dialog is a
 * decision, and an error banner in response to a deliberate cancel is the kind of
 * thing that makes people distrust the button.
 */
export async function googleCredential(): Promise<string | null> {
  if (!GOOGLE_CLIENT_ID) {
    return null;
  }
  const gis = await loadGis();

  return new Promise<string | null>(resolve => {
    let settled = false;
    const finish = (value: string | null) => {
      if (!settled) {
        settled = true;
        resolve(value);
      }
    };

    gis.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: response => finish(response.credential ?? null),
      // Signing a user in because a browser remembered them, without them pressing
      // anything, is not what this button promises.
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    gis.accounts.id.prompt(notification => {
      // GIS reports "not displayed" / "skipped" through this listener and then never
      // calls the callback at all, so without this branch the promise would hang and
      // the button would spin forever.
      const n = notification as {
        isNotDisplayed?: () => boolean;
        isSkippedMoment?: () => boolean;
      };
      if (n.isNotDisplayed?.() || n.isSkippedMoment?.()) {
        finish(null);
      }
    });
  });
}
